import React, { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { TableRowData } from '../../../../../types/common.types';
import { Member, MemberBadge, TrackingDeviceAssignment } from '../../../../../types/entity.types';

import { ARCHIVED } from '../../../../../utils/constants';
import { formatUsername } from '../../../../../utils/util';

import Button from '../../../../common/button/Button';
import FilePicker from '../../../../common/input/file-picker/FilePicker';
import Table from '../../../../common/table/Table';
import MemberModal from '../member-modal/MemberModal';

import { ReactComponent as TeamIcon } from '../../../../../resources/icons/leaderboard-team.svg';

import './member-table.scss';

type DataTemplate = {
  emailAddress: string;
  phoneNumber: string;
  name: string;
  paid: string;
  badges: string;
  team: string;
  username: string;
};

type MappedMember = {
  externalReference: string;
  firstName: string | undefined;
  lastName: string | undefined;
  emailAddress: string;
  phoneNumber: string;
  name: string;
  paid: boolean;
  staff: boolean;
  badges: string;
  memberBadges: MemberBadge[];
  team: string | undefined;
  username: undefined | string;
  deviceId?: string;
};

const DATA_TEMPLATE: DataTemplate = {
  name: '',
  emailAddress: '',
  username: '',
  phoneNumber: '',
  team: '',
  badges: '',
  paid: ''
};

const HIDDEN_COLUMNS = [
  'externalReference',
  'systemUserExternalReference',
  'firstName',
  'lastName',
  'memberBadges',
  'deviceId'
];

type Props = {
  members: Member[];
  createMember: (memberToSave: Member, onSuccess: () => void) => void;
  updateMember: (memberToSave: Member, close?: () => void) => void;
  deleteMember: (externalReference: string, onSuccess: () => void) => void;
  uploadMemberExcel: (memberExcel: File) => void;
  downloadMemberExcel: (event: React.MouseEvent<HTMLButtonElement>) => void;
  sendMemberActivationCommunications: (externalReference: string, sendSMS: boolean, sendEmail: boolean) => void;
  createTrackingDeviceAssignment: (
    systemUserExternalReference: string,
    deviceId: string,
    onSuccess?: (data: TrackingDeviceAssignment) => void
  ) => void;
  retrieveTrackingDeviceAssignments: (
    systemUserExternalReference: string,
    onSuccess?: (data: TrackingDeviceAssignment[]) => void
  ) => void;
};

const MemberTable = ({
  members,
  createMember,
  updateMember,
  deleteMember,
  uploadMemberExcel,
  downloadMemberExcel,
  sendMemberActivationCommunications,
  createTrackingDeviceAssignment,
  retrieveTrackingDeviceAssignments
}: Props) => {
  const [selectedMemberExternalReference, setSelectedMemberExternalReference] = useState<string>();
  const [isAddingMember, setIsAddingMember] = useState(false);

  const fileRef = useRef<HTMLInputElement | null>(null);

  const { status } = useParams();

  const member = useMemo(() => {
    if (isAddingMember) return {};

    return members.find((member) => member.externalReference === selectedMemberExternalReference);
  }, [isAddingMember, selectedMemberExternalReference, members]);

  const columnFunctions = useMemo(
    () => ({
      staff: (selectedMember: Member) => {
        if (status === ARCHIVED) return;

        const staff = selectedMember.staff ?? false;

        delete selectedMember.name;
        delete selectedMember.staff;
        delete selectedMember.badges;

        const member: Member = {
          ...selectedMember,
          isStaff: !staff
        };

        updateMember(member);
      },
      paid: (selectedMember: Member) => {
        if (status === ARCHIVED) return;

        const paid = !!selectedMember.paid;
        const staff = !!selectedMember.staff;

        delete selectedMember.name;
        delete selectedMember.staff;
        delete selectedMember.badges;

        const member: Member = {
          ...selectedMember,
          isStaff: staff,
          paid: !paid
        };

        updateMember(member);
      }
    }),
    [updateMember, status]
  );

  const mappedMembers = useMemo(() => {
    const mappedMembers: MappedMember[] = members.map((member) => ({
      name: `${member.firstName} ${member.lastName}`,
      emailAddress: member.emailAddress || '',
      username: formatUsername(member.username),
      phoneNumber: member.phoneNumber || '',
      badges: String(member.badgeCount) || '',
      memberBadges: member.memberBadges || [],
      team: member.teamNames?.join(', '),
      staff: !!member.isStaff,
      paid: !!member.paid,
      externalReference: member.externalReference ?? '',
      firstName: member.firstName,
      lastName: member.lastName,
      deviceId: member.deviceId
    }));

    return mappedMembers.sort((memberOne, memberTwo) => {
      if (memberOne.name > memberTwo.name) return 1;
      if (memberOne.name < memberTwo.name) return -1;
      return 0;
    });
  }, [members]);

  const handleMemberClick = (member: TableRowData) => {
    if (status === ARCHIVED) return;

    setIsAddingMember(false);
    setSelectedMemberExternalReference(member.externalReference as string);
  };

  const onAddMemberClick = () => {
    setIsAddingMember(true);
    setSelectedMemberExternalReference(undefined);
  };

  const handleMemberModalClose = () => {
    setIsAddingMember(false);
    setSelectedMemberExternalReference(undefined);
  };

  const renderBottomActions = () => {
    return (
      <div className="member-table__bottom-actions">
        {status !== ARCHIVED && (
          <>
            <Button text="Add Member" onClick={onAddMemberClick} />
            <FilePicker
              triggerElement={<Button text="Upload .xlsx" onClick={() => fileRef.current?.click()} />}
              fileRef={fileRef}
              onChange={({ target }) => target.files?.[0] && uploadMemberExcel(target.files[0])}
            />
          </>
        )}
        <Button text="Download .xlsx" onClick={downloadMemberExcel} />
      </div>
    );
  };

  const renderTeamIcon = (member: TableRowData) => {
    if (!member.team) return <td className="member-table__team" />;

    return (
      <td className="member-table__team">
        <TeamIcon className="member-table__team-icon" />
        <span className="member-table__team-label">{member.team}</span>
      </td>
    );
  };

  return (
    <>
      <section className="member-table" data-testid="member-table">
        <Table
          data={mappedMembers}
          dataTemplate={DATA_TEMPLATE}
          hiddenColumns={HIDDEN_COLUMNS}
          rowsPerPage={10}
          handleRowClick={handleMemberClick}
          columnFunctions={columnFunctions}
          bottomActions={renderBottomActions()}
          columnRenderFunctions={{
            team: renderTeamIcon
          }}
          filter
          onDeleteRowClick={undefined}
        />
      </section>

      <MemberModal
        member={member}
        close={handleMemberModalClose}
        createMember={createMember}
        updateMember={updateMember}
        deleteMember={deleteMember}
        sendMemberActivationCommunications={sendMemberActivationCommunications}
        createTrackingDeviceAssignment={createTrackingDeviceAssignment}
        retrieveTrackingDeviceAssignments={retrieveTrackingDeviceAssignments}
      />
    </>
  );
};

export default MemberTable;
